import React, { useState, useEffect } from 'react';
import { adminApi } from '../lib/adminAuth';
import { toast } from 'sonner';
import {
  CreditCard, Plus, RefreshCw, Search, Filter, Download,
  Banknote, Building2, Smartphone, FileCheck, AlertCircle, Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { formatDate, getTodayLocal } from '../lib/dateUtils';
import { HeaderSkeleton, MetricCardsSkeleton, TableSkeleton } from './skeletons/Skeletons';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Textarea } from './ui/textarea';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const paymentMethods = [
  { value: 'bank_transfer', label: 'Bank Transfer', icon: Building2 },
  { value: 'upi', label: 'UPI', icon: Smartphone },
  { value: 'cheque', label: 'Cheque', icon: FileCheck },
  { value: 'cash', label: 'Cash', icon: Banknote },
  { value: 'card', label: 'Card', icon: CreditCard }
];

// Record Payment Modal
function RecordPaymentModal({ open, onClose, invoices, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    invoice_id: '',
    amount: '',
    payment_date: getTodayLocal(),
    payment_method: 'bank_transfer',
    reference_number: '',
    notes: ''
  });

  const selectedInvoice = invoices.find(i => i._id === formData.invoice_id);
  const outstanding = selectedInvoice ? selectedInvoice.total - (selectedInvoice.amount_received || 0) : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.invoice_id || !formData.amount) {
      toast.error('Please select invoice and enter amount');
      return;
    }

    if (parseFloat(formData.amount) > outstanding) {
      toast.error(`Amount exceeds outstanding balance (${formatCurrency(outstanding)})`);
      return;
    }

    try {
      setLoading(true);
      await adminApi.post('/api/admin/accounting/payments/record', {
        ...formData,
        amount: parseFloat(formData.amount)
      });
      toast.success('Payment recorded successfully');
      onSuccess();
      onClose();
      setFormData({
        invoice_id: '',
        amount: '',
        payment_date: getTodayLocal(),
        payment_method: 'bank_transfer',
        reference_number: '',
        notes: ''
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-teal-600" />
            Record Payment
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-neutral-700">Invoice *</label>
            <Select value={formData.invoice_id} onValueChange={(v) => setFormData({...formData, invoice_id: v})}>
              <SelectTrigger>
                <SelectValue placeholder="Select invoice" />
              </SelectTrigger>
              <SelectContent>
                {invoices.filter(i => i.status !== 'paid' && i.status !== 'cancelled').map(inv => (
                  <SelectItem key={inv._id} value={inv._id}>
                    {inv.invoice_number} - {inv.client_name} ({formatCurrency(inv.total - (inv.amount_received || 0))} due)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedInvoice && (
            <div className="bg-white/40 p-3 rounded-xl text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-500">Invoice Total:</span>
                <span className="font-medium">{formatCurrency(selectedInvoice.total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-500">Already Received:</span>
                <span className="text-emerald-600">{formatCurrency(selectedInvoice.amount_received || 0)}</span>
              </div>
              <div className="flex justify-between border-t border-white/40 pt-2 mt-2">
                <span className="font-medium text-neutral-700">Outstanding:</span>
                <span className="font-bold text-red-600">{formatCurrency(outstanding)}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-neutral-700">Amount (₹) *</label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="Enter amount"
                max={outstanding}
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700">Payment Date</label>
              <Input
                type="date"
                value={formData.payment_date}
                onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-neutral-700">Payment Method</label>
            <Select value={formData.payment_method} onValueChange={(v) => setFormData({...formData, payment_method: v})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {paymentMethods.map(pm => (
                  <SelectItem key={pm.value} value={pm.value}>
                    {pm.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium text-neutral-700">Reference Number</label>
            <Input
              value={formData.reference_number}
              onChange={(e) => setFormData({...formData, reference_number: e.target.value})}
              placeholder="Transaction ID, Cheque No., etc."
            />
          </div>

          <div>
            <label className="text-sm font-medium text-neutral-700">Notes</label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Additional notes"
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
              Record Payment
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Main Payments Component
export default function PaymentRecording() {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [paidInvoiceCount, setPaidInvoiceCount] = useState(0);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateFilter.from) params.from_date = dateFilter.from;
      if (dateFilter.to) params.to_date = dateFilter.to;
      
      const [paymentsRes, invoicesRes] = await Promise.all([
        adminApi.get('/api/admin/accounting/payments', { params }),
        adminApi.get('/api/admin/accounting/invoices', { params: { limit: 10000 } })
      ]);
      setPayments(paymentsRes.data.payments || []);
      const invoicesData = invoicesRes.data?.invoices || invoicesRes.data || [];
      const invoicesList = Array.isArray(invoicesData) ? invoicesData : [];
      setInvoices(invoicesList);
      // Count paid invoices to detect if migration is needed
      setPaidInvoiceCount(invoicesList.filter(i => i.status === 'paid' || i.status === 'partially_paid').length);
    } catch (err) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateFilter]);

  const handleGeneratePayments = async () => {
    setGenerating(true);
    try {
      const res = await adminApi.post('/api/admin/accounting/fix-paid-invoices');
      const data = res.data;
      toast.success(`Generated ${data.payments_created || 0} payment records from ${data.total_paid_invoices || 0} paid invoices`);
      await fetchData();
    } catch (err) {
      toast.error('Failed to generate payment records');
    } finally {
      setGenerating(false);
    }
  };

  const filteredPayments = payments.filter(p =>
    p.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.reference_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalReceived = payments.reduce((sum, p) => sum + (p.amount || 0), 0);

  const methodIcons = {
    bank_transfer: Building2,
    upi: Smartphone,
    cheque: FileCheck,
    cash: Banknote,
    card: CreditCard
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="payment-recording-loading">
        <HeaderSkeleton />
        <MetricCardsSkeleton count={3} />
        <TableSkeleton rows={6} columns={5} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-recording">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a2e]">Payment Recording</h1>
          <p className="text-sm text-neutral-500">Track payments against invoices</p>
        </div>
        <Button onClick={() => setShowRecordModal(true)} data-testid="record-payment-btn">
          <Plus className="w-4 h-4 mr-2" />
          Record Payment
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="Search payments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={dateFilter.from}
            onChange={(e) => setDateFilter({...dateFilter, from: e.target.value})}
            className="w-36"
            placeholder="From"
          />
          <span className="text-neutral-400">to</span>
          <Input
            type="date"
            value={dateFilter.to}
            onChange={(e) => setDateFilter({...dateFilter, to: e.target.value})}
            className="w-36"
            placeholder="To"
          />
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="glass-card p-4">
          <p className="text-sm text-neutral-500">Total Payments</p>
          <p className="text-2xl font-bold text-[#1a1a2e]">{payments.length}</p>
        </div>
        <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-4">
          <p className="text-sm text-emerald-600">Total Received</p>
          <p className="text-2xl font-bold text-emerald-700">{formatCurrency(totalReceived)}</p>
        </div>
        <div className="bg-blue-50 rounded-xl border border-blue-100 p-4">
          <p className="text-sm text-blue-600">Most Used Method</p>
          <p className="text-lg font-bold text-blue-700">
            {payments.length > 0 
              ? paymentMethods.find(m => m.value === payments[0]?.payment_method)?.label || 'Bank Transfer'
              : '-'}
          </p>
        </div>
      </div>

      {/* Payments Table */}
      <div className="soft-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/40 border-b border-white/40">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Invoice</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Client</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-neutral-700">Method</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Reference</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/30">
              {filteredPayments.map((payment) => {
                const MethodIcon = methodIcons[payment.payment_method] || CreditCard;
                return (
                  <tr key={payment.id} className="hover:bg-white/60">
                    <td className="px-4 py-3 text-neutral-500">{payment.payment_date ? formatDate(payment.payment_date) : '-'}</td>
                    <td className="px-4 py-3 font-medium text-[#1a1a2e]">{payment.invoice_number}</td>
                    <td className="px-4 py-3 text-neutral-700">{payment.client_name}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <MethodIcon className="w-4 h-4 text-neutral-500" />
                        <span className="text-sm text-neutral-600">
                          {paymentMethods.find(m => m.value === payment.payment_method)?.label || payment.payment_method}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-500 font-mono text-sm">{payment.reference_number || '-'}</td>
                    <td className="px-4 py-3 text-right font-bold text-emerald-600">{formatCurrency(payment.amount)}</td>
                  </tr>
                );
              })}
              {filteredPayments.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center">
                    <div className="space-y-3">
                      <p className="text-neutral-500">No payments found</p>
                      {payments.length === 0 && paidInvoiceCount > 0 && (
                        <div className="inline-flex flex-col items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-6 py-4" data-testid="generate-payments-prompt">
                          <div className="flex items-center gap-2 text-amber-700 text-sm">
                            <AlertCircle className="w-4 h-4" />
                            <span>{paidInvoiceCount} paid invoice{paidInvoiceCount > 1 ? 's' : ''} found without payment records</span>
                          </div>
                          <Button
                            size="sm"
                            onClick={handleGeneratePayments}
                            disabled={generating}
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid="generate-payments-btn"
                          >
                            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                            {generating ? 'Generating...' : 'Generate Payment Records'}
                          </Button>
                          <p className="text-xs text-amber-600">This will create payment records for all invoices marked as Paid</p>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Record Modal */}
      <RecordPaymentModal
        open={showRecordModal}
        onClose={() => setShowRecordModal(false)}
        invoices={invoices}
        onSuccess={fetchData}
      />
    </div>
  );
}
